import { useEffect, useMemo, useState } from "react";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Download, Trash2, FileText, AlertCircle, PoundSterling, Clock, CheckCircle2, FileSpreadsheet, History } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EMPTY = {
  customer_id_display: "",
  recipient_name: "",
  recipient_company: "",
  recipient_address_lines: ["", "", ""],
  issue_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  payment_terms: "On receipt",
  status: "sent",
  line_items: [{ description: "", amount: 0 }],
  paid_amount: 0,
  notes: "",
};

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("all");
  const [fyFilter, setFyFilter] = useState("all"); // "all" | "YYYY" (UK FY start year)
  const [editing, setEditing] = useState(null);
  const [paymentsFor, setPaymentsFor] = useState(null); // {invoice_id, invoice_number, total, ...}
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = status !== "all" ? { params: { status } } : undefined;
    const [r, s] = await Promise.all([api.get("/invoices", params), api.get("/invoices/summary")]);
    setRows(r.data); setSummary(s.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  // UK financial year: 6 April YYYY → 5 April YYYY+1. Any date in Jan-Apr 5 belongs to FY (year-1).
  const fyStartYear = (isoDate) => {
    if (!isoDate) return null;
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return null;
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth(); // 0-based
    const day = d.getUTCDate();
    // Before 6 April → belongs to previous FY
    if (m < 3 || (m === 3 && day < 6)) return y - 1;
    return y;
  };

  const fyLabel = (startYear) => `FY ${startYear}/${String((startYear + 1) % 100).padStart(2, "0")}`;

  const availableFYs = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const fy = fyStartYear(r.issue_date);
      if (fy != null) set.add(fy);
    });
    // Also ensure current FY is offered even if no rows yet
    const nowFY = fyStartYear(new Date().toISOString());
    if (nowFY != null) set.add(nowFY);
    return Array.from(set).sort((a, b) => b - a);
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (fyFilter === "all") return rows;
    const y = Number(fyFilter);
    return rows.filter((r) => fyStartYear(r.issue_date) === y);
  }, [rows, fyFilter]);

  const fyTotals = useMemo(() => {
    let billed = 0, paid = 0, unpaid = 0, overdue = 0, count = 0;
    filteredRows.forEach((r) => {
      const t = Number(r.total || 0);
      const p = Number(r.paid_amount || 0);
      const bal = Math.max(0, t - p);
      billed += t;
      paid += Math.min(p, t);
      count += 1;
      if (r.status !== "void" && r.status !== "paid") unpaid += bal;
      if (r.is_overdue) overdue += bal;
    });
    return { billed, paid, unpaid, overdue, count };
  }, [filteredRows]);

  const editorTotal = useMemo(
    () => (editing?.line_items || []).reduce((a, i) => a + Number(i.amount || 0), 0),
    [editing]
  );

  const addLine = () => setEditing((e) => ({ ...e, line_items: [...e.line_items, { description: "", amount: 0 }] }));
  const removeLine = (i) => setEditing((e) => ({ ...e, line_items: e.line_items.filter((_, j) => j !== i) }));
  const updateLine = (i, patch) => setEditing((e) => ({ ...e, line_items: e.line_items.map((it, j) => j === i ? { ...it, ...patch } : it) }));

  const save = async () => {
    if (!editing.recipient_name && !editing.recipient_company) { toast.error("Add a recipient"); return; }
    if (!editing.line_items?.length) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        recipient_address_lines: editing.recipient_address_lines.filter(Boolean),
        line_items: editing.line_items.filter((i) => i.description || Number(i.amount || 0)),
      };
      if (editing.invoice_id) {
        await api.patch(`/invoices/${editing.invoice_id}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", payload);
        toast.success("Invoice created");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const downloadPdf = (inv) => {
    const url = `${API}/invoices/${inv.invoice_id}/pdf`;
    window.open(url, "_blank");
  };

  const markPaid = async (inv) => {
    try {
      await api.patch(`/invoices/${inv.invoice_id}`, { status: "paid", paid_amount: Number(inv.total || 0) });
      toast.success(`${inv.invoice_number} marked as paid`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark as paid"); }
  };

  const savePaidAmount = async (inv, raw) => {
    const parsed = raw === "" || raw == null ? 0 : Number(raw);
    if (Number.isNaN(parsed) || parsed < 0) { toast.error("Enter a positive number"); return; }
    const rounded = Math.round(parsed * 100) / 100;
    if (rounded === Number(inv.paid_amount || 0)) return; // no-op
    try {
      const r = await api.patch(`/invoices/${inv.invoice_id}`, { paid_amount: rounded });
      const balance = Math.max(0, Number(r.data.total || 0) - Number(r.data.paid_amount || 0));
      if (r.data.status === "paid") {
        toast.success(`${r.data.invoice_number} fully paid`);
      } else if (rounded > 0) {
        toast.success(`Recorded ${money(rounded)} — ${money(balance)} balance remaining`);
      } else {
        toast.success(`Payment cleared for ${r.data.invoice_number}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record payment"); }
  };

  const exportCsv = () => {
    if (filteredRows.length === 0) { toast.error("Nothing to export"); return; }
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["Invoice #", "Issue date", "Due date", "Customer ID", "Recipient name", "Recipient company", "Total (GBP)", "Paid (GBP)", "Balance (GBP)", "Status", "Paid date", "Payment terms"];
    const lines = [headers.join(",")];
    filteredRows.forEach((r) => {
      const paid = Number(r.paid_amount || 0);
      const bal = Math.max(0, Number(r.total || 0) - paid);
      lines.push([
        r.invoice_number,
        r.issue_date ? r.issue_date.slice(0, 10) : "",
        r.due_date ? r.due_date.slice(0, 10) : "",
        r.customer_id_display || "",
        r.recipient_name || "",
        r.recipient_company || "",
        Number(r.total || 0).toFixed(2),
        paid.toFixed(2),
        bal.toFixed(2),
        r.status || "",
        r.paid_at ? r.paid_at.slice(0, 10) : "",
        r.payment_terms || "",
      ].map(esc).join(","));
    });
    const csv = "\uFEFF" + lines.join("\n"); // BOM so Excel opens £ correctly
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = fyFilter === "all" ? "all" : `FY${fyFilter}-${String((Number(fyFilter) + 1) % 100).padStart(2, "0")}`;
    a.href = url;
    a.download = `taxegy-invoices-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredRows.length} invoice${filteredRows.length === 1 ? "" : "s"}`);
  };

  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const ids = filteredRows.map((r) => r.invoice_id);
    const all = ids.length > 0 && ids.every((i) => s.has(i));
    if (all) { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; }
    const n = new Set(s); ids.forEach((i) => n.add(i)); return n;
  });
  const bulkDelete = async () => {
    try {
      const r = await api.post("/invoices/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted} invoice${r.data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set()); setBulkDeleteOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openNew = () => setEditing({ ...EMPTY, recipient_address_lines: ["", "", ""] });
  const openEdit = (inv) => setEditing({ ...inv, issue_date: inv.issue_date?.slice(0, 10), due_date: inv.due_date?.slice(0, 10) || "", recipient_address_lines: [...(inv.recipient_address_lines || []), "", "", ""].slice(0, 4) });

  return (
    <div>
      <PageHeader
        testid="page-invoices"
        title="Invoices"
        subtitle="Log work, generate PDF invoices, track what's overdue"
        actions={
          <div className="flex items-center gap-2">
            <Button data-testid="export-csv-btn" variant="outline" onClick={exportCsv} className="rounded-md">
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export CSV
            </Button>
            <Button data-testid="new-invoice-btn" onClick={openNew} className="rounded-md bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" />New invoice</Button>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile testid="invoices-tile-total"    label="Total invoices" value={summary.total_invoices} icon={FileText} />
            <Tile testid="invoices-tile-unpaid"   label="Unpaid" value={summary.unpaid_count} sub={money(summary.unpaid_amount)} icon={Clock} tone="amber" />
            <Tile testid="invoices-tile-overdue"  label="Overdue" value={summary.overdue_count} sub={money(summary.overdue_amount)} icon={AlertCircle} tone="rose" />
            <Tile testid="invoices-tile-billed"   label="Billed (unpaid)" value={money(summary.unpaid_amount)} icon={PoundSterling} />
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="invoices-status-filter" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Unpaid</SelectItem>
              <SelectItem value="partial">Part-paid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="void">Void</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fyFilter} onValueChange={setFyFilter}>
            <SelectTrigger data-testid="invoices-fy-filter" className="w-56 bg-white"><SelectValue placeholder="Financial year" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All financial years</SelectItem>
              {availableFYs.map((y) => (
                <SelectItem key={y} value={String(y)} data-testid={`invoices-fy-option-${y}`}>{fyLabel(y)} · 6 Apr {y} → 5 Apr {y + 1}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-slate-500" data-testid="invoices-shown-count">{filteredRows.length} shown</div>
          {fyFilter !== "all" && (
            <div className="ml-auto flex items-center gap-4 text-xs text-slate-600" data-testid="invoices-fy-summary">
              <span>Billed: <span className="font-mono font-semibold text-slate-900">{money(fyTotals.billed)}</span></span>
              <span>Paid: <span className="font-mono font-semibold text-emerald-700">{money(fyTotals.paid)}</span></span>
              <span>Unpaid: <span className="font-mono font-semibold text-amber-700">{money(fyTotals.unpaid)}</span></span>
              {fyTotals.overdue > 0 && <span>Overdue: <span className="font-mono font-semibold text-rose-700">{money(fyTotals.overdue)}</span></span>}
            </div>
          )}
        </div>

        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {selected.size > 0 && (
            <div className="bg-slate-900 text-slate-100 px-4 py-2 flex items-center justify-between text-sm" data-testid="invoices-bulk-bar">
              <span><span className="font-semibold">{selected.size}</span> selected</span>
              <div className="flex items-center gap-3">
                <button onClick={() => setSelected(new Set())} className="text-slate-300 hover:text-white text-xs">Clear</button>
                <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)} className="h-8" data-testid="invoices-bulk-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete selected</Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="w-10 px-4 py-2"><Checkbox data-testid="invoices-select-all" checked={filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.invoice_id))} onCheckedChange={toggleAll} /></th>
                <th className="text-left px-4 py-2">Invoice #</th>
                <th className="text-left px-4 py-2">Recipient</th>
                <th className="text-left px-4 py-2">Issued</th>
                <th className="text-left px-4 py-2">Due</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-right px-4 py-2 w-32">Paid</th>
                <th className="text-right px-4 py-2">Balance</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && <tr><td colSpan={10} className="px-4 py-16 text-center text-slate-500"><FileText className="h-6 w-6 mx-auto text-slate-300 mb-2" />{rows.length === 0 ? "No invoices yet — click New invoice to log your first one." : "No invoices in the selected financial year."}</td></tr>}
              {filteredRows.map((r) => (
                <tr key={r.invoice_id} data-testid="invoice-row" className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(r.invoice_id) ? "bg-slate-50" : ""}`}>
                  <td className="px-4 py-3"><Checkbox data-testid={`invoice-row-select-${r.invoice_id}`} checked={selected.has(r.invoice_id)} onCheckedChange={() => toggleOne(r.invoice_id)} /></td>
                  <td className="px-4 py-3"><button onClick={() => openEdit(r)} className="font-mono text-slate-900 hover:text-blue-600 font-medium">{r.invoice_number}</button></td>
                  <td className="px-4 py-3"><div className="text-slate-900">{r.recipient_company || r.recipient_name || "—"}</div>{r.recipient_company && r.recipient_name && <div className="text-xs text-slate-500">{r.recipient_name}</div>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.issue_date)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.due_date ? fmtDate(r.due_date) : "—"}</td>
                  <td className="px-4 py-3 text-right font-medium">{money(r.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={Number(r.paid_amount || 0).toFixed(2)}
                      data-testid={`inv-paid-input-${r.invoice_id}`}
                      onBlur={(e) => savePaidAmount(r, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                      className="h-8 w-24 text-right font-mono text-xs ml-auto bg-white"
                      key={`${r.invoice_id}-${r.paid_amount || 0}`}
                    />
                  </td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${Number(r.balance ?? (r.total - (r.paid_amount || 0))) <= 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid={`inv-balance-${r.invoice_id}`}>
                    {money(Math.max(0, Number(r.balance ?? (r.total - (r.paid_amount || 0)))))}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={r.status === "sent" ? "unpaid" : r.status} label={r.status === "partial" ? "part-paid" : r.status === "sent" ? "unpaid" : undefined} />
                      {r.is_overdue && <span className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold" data-testid="overdue-marker">Overdue</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setPaymentsFor(r)}
                        title="Payment history"
                        data-testid={`payments-history-${r.invoice_id}`}
                        className="p-1.5 text-slate-500 hover:text-blue-600 rounded"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      {r.status !== "paid" && r.status !== "void" && (
                        <button
                          onClick={() => markPaid(r)}
                          title="Mark as paid"
                          data-testid={`mark-paid-${r.invoice_id}`}
                          className="p-1.5 text-slate-500 hover:text-emerald-600 rounded"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => downloadPdf(r)} title="Download PDF" data-testid={`download-pdf-${r.invoice_id}`} className="p-1.5 text-slate-500 hover:text-slate-900 rounded"><Download className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="invoice-editor" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.invoice_id ? `Edit ${editing.invoice_number}` : "New invoice"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Recipient name</Label><Input data-testid="inv-recipient-name" value={editing.recipient_name} onChange={(e) => setEditing({ ...editing, recipient_name: e.target.value })} placeholder="Tyson Mathurine" /></div>
                <div><Label>Recipient company</Label><Input data-testid="inv-recipient-company" value={editing.recipient_company} onChange={(e) => setEditing({ ...editing, recipient_company: e.target.value })} placeholder="Advanced Home Providers Ltd" /></div>
              </div>
              <div>
                <Label>Recipient address (one line per row)</Label>
                {editing.recipient_address_lines.map((ln, i) => (
                  <Input key={i} className="mt-1" placeholder={i === 0 ? "Street" : i === 1 ? "Town, Postcode" : "Country / extra"} value={ln} onChange={(e) => setEditing({ ...editing, recipient_address_lines: editing.recipient_address_lines.map((x, j) => j === i ? e.target.value : x) })} />
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label>Customer ID</Label><Input data-testid="inv-customer-id" value={editing.customer_id_display} onChange={(e) => setEditing({ ...editing, customer_id_display: e.target.value })} placeholder="ADV001" /></div>
                <div><Label>Issue date</Label><Input data-testid="inv-issue-date" type="date" value={editing.issue_date} onChange={(e) => setEditing({ ...editing, issue_date: e.target.value })} /></div>
                <div><Label>Due date</Label><Input data-testid="inv-due-date" type="date" value={editing.due_date} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} /></div>
                <div><Label>Payment terms</Label><Input data-testid="inv-terms" value={editing.payment_terms} onChange={(e) => setEditing({ ...editing, payment_terms: e.target.value })} /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Line items (description + amount)</Label>
                  <button type="button" onClick={addLine} className="text-xs text-slate-600 hover:text-slate-900 font-medium" data-testid="inv-add-line"><Plus className="h-3 w-3 inline mr-1" />Add line</button>
                </div>
                <div className="space-y-2">
                  {editing.line_items.map((it, i) => (
                    <div key={i} className="grid grid-cols-[1fr,140px,32px] gap-2">
                      <Textarea data-testid={`inv-line-desc-${i}`} rows={2} value={it.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Y.E 31/03/25&#10;Bookkeeping, accounts and CT preparation …" />
                      <Input data-testid={`inv-line-amount-${i}`} type="number" step="0.01" value={it.amount} onChange={(e) => updateLine(i, { amount: e.target.value })} placeholder="0.00" />
                      <button type="button" onClick={() => removeLine(i)} className="text-slate-400 hover:text-rose-600" title="Remove line"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <div className="flex items-center justify-between">
                    <Label className="mb-0">Paid so far (£)</Label>
                    <Input
                      data-testid="inv-editor-paid-amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editing.paid_amount ?? 0}
                      onChange={(e) => setEditing({ ...editing, paid_amount: e.target.value })}
                      className="h-8 w-28 text-right font-mono"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 uppercase tracking-wider text-xs">Total</span>
                    <span className="font-mono font-semibold text-slate-900 text-base" data-testid="inv-total">{money(editorTotal)}</span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-4 pt-1">
                    <span className="text-xs text-slate-500">Balance:</span>
                    <span
                      data-testid="inv-editor-balance"
                      className={`font-mono font-semibold ${Math.max(0, editorTotal - Number(editing.paid_amount || 0)) <= 0 ? "text-emerald-700" : "text-amber-700"}`}
                    >
                      {money(Math.max(0, editorTotal - Number(editing.paid_amount || 0)))}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger data-testid="inv-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Unpaid</SelectItem>
                      <SelectItem value="partial">Part-paid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="void">Void</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Internal notes (not on PDF)</Label><Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
              </div>
              <div className="text-[11px] text-slate-500">Bank details, sender address and footer come from Settings. Update them there before generating PDFs.</div>
            </div>
          )}
          <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-background border-t border-slate-200">
            {editing?.invoice_id && <Button variant="outline" onClick={() => downloadPdf(editing)} data-testid="inv-download-pdf"><Download className="h-4 w-4 mr-1.5" />Download PDF</Button>}
            <Button data-testid="inv-save-btn" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{saving ? "Saving…" : (editing?.invoice_id ? "Save changes" : "Create invoice")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="invoices-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} invoice{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>Permanently removes these invoices from your records. Cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="invoices-bulk-delete-confirm">Yes, delete {selected.size}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PaymentsDialog invoice={paymentsFor} onClose={() => setPaymentsFor(null)} onChange={load} />
    </div>
  );
}

function PaymentsDialog({ invoice, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), method: "Bank transfer", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!invoice) { setData(null); return; }
    api.get(`/invoices/${invoice.invoice_id}/payments`).then((r) => setData(r.data)).catch(() => setData(null));
    setForm({ amount: "", date: new Date().toISOString().slice(0, 10), method: "Bank transfer", notes: "" });
  }, [invoice]);

  if (!invoice) return null;

  const balance = data ? Math.max(0, Number(data.balance || 0)) : 0;

  const addPayment = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a payment amount"); return; }
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.invoice_id}/payments`, { amount: amt, date: form.date, method: form.method, notes: form.notes || undefined });
      const r = await api.get(`/invoices/${invoice.invoice_id}/payments`);
      setData(r.data);
      setForm({ amount: "", date: new Date().toISOString().slice(0, 10), method: "Bank transfer", notes: "" });
      toast.success(`Recorded £${amt.toFixed(2)} payment`);
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const deletePayment = async (payment_id) => {
    if (!window.confirm("Remove this payment entry?")) return;
    try {
      await api.delete(`/invoices/${invoice.invoice_id}/payments/${payment_id}`);
      const r = await api.get(`/invoices/${invoice.invoice_id}/payments`);
      setData(r.data);
      toast.success("Payment removed");
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const METHODS = ["Bank transfer", "Cash", "Cheque", "Card", "Direct debit", "Other"];
  const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Dialog open={!!invoice} onOpenChange={onClose}>
      <DialogContent data-testid="payments-dialog" className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Payments — {invoice.invoice_number}</DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center border border-slate-200 rounded-md p-3 bg-slate-50">
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</div><div className="font-mono text-lg font-semibold" data-testid="pay-total">{money(data.total)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Paid</div><div className="font-mono text-lg font-semibold text-emerald-700" data-testid="pay-paid">{money(data.paid_amount)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Balance</div><div className={`font-mono text-lg font-semibold ${balance <= 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid="pay-balance">{money(balance)}</div></div>
            </div>

            {balance > 0 && (
              <div className="border border-slate-200 rounded-md p-3 space-y-3" data-testid="pay-add-form">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-600">Record a payment</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Amount (£)</Label>
                    <Input data-testid="pay-input-amount" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={balance.toFixed(2)} />
                  </div>
                  <div>
                    <Label className="text-xs">Date received</Label>
                    <Input data-testid="pay-input-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Method</Label>
                    <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                      <SelectTrigger data-testid="pay-input-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input data-testid="pay-input-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Ref 1234" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={addPayment} disabled={busy} data-testid="pay-add-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="h-3.5 w-3.5 mr-1" />Record payment</Button>
                </div>
              </div>
            )}

            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2">Payment history</div>
              {data.payments.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-md">No payments recorded yet.</div>
              ) : (
                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md bg-white" data-testid="pay-history-list">
                  {data.payments.map((p) => (
                    <li key={p.payment_id} data-testid={`pay-entry-${p.payment_id}`} className="px-3 py-2 flex items-center gap-3 text-sm">
                      <div className="font-mono text-xs text-slate-500 w-24">{p.date}</div>
                      <div className="font-mono font-semibold text-emerald-700 w-24">{money(p.amount)}</div>
                      <div className="text-xs text-slate-600 flex-1 min-w-0">
                        <span className="font-medium">{p.method}</span>
                        {p.notes && <span className="text-slate-500"> · {p.notes}</span>}
                      </div>
                      <button onClick={() => deletePayment(p.payment_id)} title="Remove entry" data-testid={`pay-delete-${p.payment_id}`} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Tile({ testid, label, value, sub, icon: Icon, tone }) {
  const toneMap = {
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose:  "border-rose-200 bg-rose-50 text-rose-900",
  };
  return (
    <div data-testid={testid} className={`border rounded-md p-4 ${toneMap[tone] || "border-slate-200 bg-white text-slate-900"}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold opacity-70">{label}</div>
        <Icon className="h-4 w-4 opacity-60" />
      </div>
      <div className="mt-2 text-2xl font-heading font-semibold">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}
